import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import Logo from "../../components/Logo";
import { Lock, ShieldCheck } from "lucide-react";

export default function AdminLoginPage() {
  const { authChecking, user, login, verifyTotp } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpToken, setTotpToken] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [step, setStep] = useState("credentials"); // "credentials" | "totp"
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (authChecking) {
    return (
      <div
        style={{ background: "#111111", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}
        data-testid="admin-login-loading"
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 32, height: 32, border: "2px solid #CC1A1A", borderTopColor: "transparent", borderRadius: "50%", animation: "rd-spin 0.8s linear infinite", margin: "0 auto 24px" }} />
          <p style={{ color: "#CC1A1A", fontFamily: "monospace", fontSize: 11, letterSpacing: "0.3em", textTransform: "uppercase" }}>
            // Checking clearance…
          </p>
          <style>{`@keyframes rd-spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    );
  }
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (step === "credentials") {
        const data = await login(email, password, rememberMe);
        if (data.status === "totp_required") {
          setTotpToken(data.totp_token);
          setStep("totp");
        } else {
          toast.success("ACCESS GRANTED");
          navigate(data.totp_enabled ? "/admin" : "/admin/totp-setup");
        }
      } else {
        await verifyTotp(totpToken, totpCode, rememberMe);
        toast.success("ACCESS GRANTED");
        navigate("/admin");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#111111]" data-testid="admin-login-page">
      {/* Left: brand panel */}
      <div className="relative hidden md:flex items-center justify-center bg-[#0B0B0B] border-r border-[#2A2A2A] scanlines overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1400&q=85&auto=format&fit=crop"
            alt=""
            className="w-full h-full object-cover mix-blend-luminosity"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#111111] via-[#111111]/60 to-[#111111]" />
        </div>
        <div className="relative z-10 p-12 text-white max-w-md">
          <div className="mb-10">
            <Logo />
          </div>
          <p className="eyebrow eyebrow-red">// EDITOR'S TERMINAL</p>
          <h1 className="font-mono text-4xl mt-4 leading-tight tracking-tight uppercase">
            Authorized personnel only.
          </h1>
          <p className="mt-6 text-[#9A8F83] font-sans text-sm leading-relaxed">
            This terminal posts deals to the restricted feed. All actions are timestamped, signed, and logged.
          </p>

          <div className="mt-10 hr-dot" />
          <p className="mt-6 text-[11px] font-mono tracking-[0.22em] uppercase text-[#555]">
            RESTRICTEDDEALS.COM · SECURE CHANNEL · ENCRYPTED
          </p>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-8 md:p-16">
        {step === "credentials" ? (
          <form onSubmit={submit} className="w-full max-w-md" data-testid="admin-login-form">
            <div className="md:hidden mb-10">
              <Logo />
            </div>

            <p className="eyebrow eyebrow-red flex items-center gap-2">
              <Lock className="w-3 h-3" /> // AUTHENTICATION
            </p>
            <h2 className="font-mono text-4xl mt-4 text-white uppercase tracking-tight">
              Enter credentials
            </h2>

            <div className="mt-10 space-y-6">
              <div>
                <label className="ed-label" htmlFor="email">Operator Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="ed-input"
                  required
                  autoComplete="email"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <label className="ed-label" htmlFor="password">Passcode</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="ed-input"
                  required
                  autoComplete="current-password"
                  data-testid="login-password-input"
                />
              </div>

              <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="remember-me-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 accent-[#CC1A1A]"
                  data-testid="remember-me-checkbox"
                />
                <span className="text-[11px] font-mono tracking-[0.18em] uppercase text-[#9A8F83]">
                  Remember me for 30 days
                </span>
              </label>

              {error && (
                <p className="text-sm text-[#CC1A1A] font-mono tracking-wider uppercase" data-testid="login-error">
                  // {error}
                </p>
              )}

              <button type="submit" disabled={loading} className="btn-rd w-full" data-testid="login-submit">
                {loading ? "AUTHENTICATING…" : "UNLOCK TERMINAL →"}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={submit} className="w-full max-w-md" data-testid="admin-totp-form">
            <div className="md:hidden mb-10">
              <Logo />
            </div>

            <p className="eyebrow eyebrow-red flex items-center gap-2">
              <ShieldCheck className="w-3 h-3" /> // TWO-FACTOR AUTH
            </p>
            <h2 className="font-mono text-4xl mt-4 text-white uppercase tracking-tight">
              Enter auth code
            </h2>
            <p className="mt-3 text-sm text-[#9A8F83] font-sans">
              Open your authenticator app and enter the 6-digit code.
            </p>

            <div className="mt-10 space-y-6">
              <div>
                <label className="ed-label" htmlFor="totp-code">6-Digit Code</label>
                <input
                  id="totp-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="ed-input tracking-[0.5em] text-center text-2xl"
                  placeholder="000000"
                  required
                  autoFocus
                  data-testid="login-totp-input"
                />
              </div>

              {error && (
                <p className="text-sm text-[#CC1A1A] font-mono tracking-wider uppercase" data-testid="login-error">
                  // {error}
                </p>
              )}

              <button type="submit" disabled={loading || totpCode.length < 6} className="btn-rd w-full" data-testid="login-totp-submit">
                {loading ? "VERIFYING…" : "VERIFY CODE →"}
              </button>
              <button
                type="button"
                onClick={() => { setStep("credentials"); setError(""); setTotpCode(""); }}
                className="w-full text-center text-xs font-mono tracking-widest uppercase text-[#9A8F83] hover:text-white transition-colors"
              >
                ← BACK
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
